// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — API Client
// Wraps all backend calls; used by React Query hooks
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import type {
  Project, Scenario, LCCAParameters, LCCAResults,
  CreateProjectRequest, UpdateProjectRequest,
  CreateScenarioRequest, UpdateScenarioRequest,
  ScenarioComparison,
} from '@lcca-nexus/shared';

// Base URL: empty string means same origin (works on CF Pages + proxy in dev)
const BASE_URL = (import.meta as any).env['VITE_API_URL'] ?? '';

const http = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// Response interceptor — unwrap { success, data } envelope
http.interceptors.response.use(
  (res) => res,
  (err) => {
    const message =
      err.response?.data?.error ?? err.message ?? 'Unknown error';
    return Promise.reject(new Error(message));
  }
);

function unwrap<T>(res: { data: { success: boolean; data: T } }): T {
  return res.data.data;
}

// ── Projects ──────────────────────────────────────────────────────────────────

export const projectsApi = {
  list: () =>
    http.get<{ success: true; data: Project[] }>('/api/projects')
      .then(unwrap),

  get: (id: string) =>
    http.get<{ success: true; data: Project & { scenarios: Scenario[] } }>(`/api/projects/${id}`)
      .then(unwrap),

  create: (body: CreateProjectRequest) =>
    http.post<{ success: true; data: Project }>('/api/projects', body)
      .then(unwrap),

  update: (id: string, body: UpdateProjectRequest) =>
    http.put<{ success: true; data: Project }>(`/api/projects/${id}`, body)
      .then(unwrap),

  delete: (id: string) =>
    http.delete(`/api/projects/${id}`).then(unwrap),
};

// ── Scenarios ─────────────────────────────────────────────────────────────────

export const scenariosApi = {
  list: (projectId: string) =>
    http.get<{ success: true; data: Scenario[] }>(`/api/projects/${projectId}/scenarios`)
      .then(unwrap),

  get: (projectId: string, id: string) =>
    http.get<{ success: true; data: Scenario }>(`/api/projects/${projectId}/scenarios/${id}`)
      .then(unwrap),

  create: (projectId: string, body: CreateScenarioRequest) =>
    http.post<{ success: true; data: Scenario }>(`/api/projects/${projectId}/scenarios`, body)
      .then(unwrap),

  update: (projectId: string, id: string, body: UpdateScenarioRequest) =>
    http.put<{ success: true; data: Scenario }>(`/api/projects/${projectId}/scenarios/${id}`, body)
      .then(unwrap),

  delete: (projectId: string, id: string) =>
    http.delete(`/api/projects/${projectId}/scenarios/${id}`).then(unwrap),

  compare: (projectId: string) =>
    http.get<{ success: true; data: ScenarioComparison }>(`/api/projects/${projectId}/compare`)
      .then(unwrap),
};

// ── Calculate (stateless) ─────────────────────────────────────────────────────

export const calculateApi = {
  calculate: (parameters: LCCAParameters) =>
    http.post<{ success: true; data: { results: LCCAResults } }>('/api/calculate', { parameters })
      .then(unwrap)
      .then(d => d.results),
};

// ── Health ────────────────────────────────────────────────────────────────────
export const healthApi = {
  ping: () => http.get('/api/health').then(r => r.data),
};
